import { useState, useEffect, useRef, useCallback } from 'react';

function useInactivityTimer(options) {
  var timeoutMs   = options.timeoutMs   || 30 * 60 * 1000;
  var countdownMs = options.countdownMs || 60 * 1000;
  var onLogout    = options.onLogout;
  var enabled     = options.enabled !== false;

  var [showWarning, setShowWarning] = useState(false);
  var [secondsLeft, setSecondsLeft] = useState(Math.floor(countdownMs / 1000));

  var inactivityTimerRef   = useRef(null);
  var countdownIntervalRef = useRef(null);
  var onLogoutRef          = useRef(onLogout);
  var showWarningRef       = useRef(false);

  // Keep refs current
  useEffect(function syncLogoutRef() { onLogoutRef.current = onLogout; }, [onLogout]);
  useEffect(function syncShowRef() { showWarningRef.current = showWarning; }, [showWarning]);

  var startCountdown = useCallback(function startCountdown() {
    var seconds = Math.floor(countdownMs / 1000);
    setShowWarning(true);
    setSecondsLeft(seconds);

    clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(function tick() {
      seconds -= 1;
      setSecondsLeft(seconds);
      if (seconds <= 0) {
        clearInterval(countdownIntervalRef.current);
        if (onLogoutRef.current) onLogoutRef.current();
      }
    }, 1000);
  }, [countdownMs]);

  var resetTimer = useCallback(function resetTimer() {
    if (showWarningRef.current) return;  // warning is visible — don't dismiss on activity
    clearTimeout(inactivityTimerRef.current);
    clearInterval(countdownIntervalRef.current);
    setShowWarning(false);
    inactivityTimerRef.current = setTimeout(startCountdown, timeoutMs);
  }, [timeoutMs, startCountdown]);

  var dismissWarning = useCallback(function dismissWarning() {
    clearInterval(countdownIntervalRef.current);
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(function setupListeners() {
    if (!enabled) return;

    var events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'wheel'];
    events.forEach(function(e) { document.addEventListener(e, resetTimer, true); });
    resetTimer();

    return function cleanup() {
      events.forEach(function(e) { document.removeEventListener(e, resetTimer, true); });
      clearTimeout(inactivityTimerRef.current);
      clearInterval(countdownIntervalRef.current);
    };
  }, [enabled, resetTimer]);

  return { showWarning: showWarning, secondsLeft: secondsLeft, dismissWarning: dismissWarning };
}

export default useInactivityTimer;
